/**
 * Actions owned by the background sync coordinator.
 */

/**
 * The single "re-fetch server-authoritative data now" signal. Features
 * that hold data the server can change behind their back (todos,
 * calendar, household, notifications, rule snapshot) listen for this.
 *
 * Replaces the previous arrangement where each of those five features
 * independently took `ws/reconnected`. That fan-out had no owner: nothing
 * could see it as one cycle, decide it was a bad moment to run, or retry
 * it — so when the tab was frozen or the host slept, all five requests
 * died on dead sockets at once and the cycle was silently dropped. The
 * coordinator (see ./saga) now owns *when* a cycle runs; the features
 * still own *what* each one fetches.
 */
export const SYNC_REFRESH = 'sync/refresh';

/**
 * The process just came back from a suspension — a wall-clock jump the
 * event loop couldn't have produced on its own (host sleep, tab freeze,
 * a long GC/debugger pause). Distinct from `tab/refocused`, which only
 * fires for visibility changes and so misses the case that hurts most:
 * a laptop sleeping with the tab in the foreground.
 */
export const APP_RESUMED = 'app/resumed';

/** What woke the sync cycle — recorded on the QPL `sync.cycle` flow. */
export type SyncReason = 'ws_reconnected' | 'app_resumed' | 'tab_refocused';

export interface SyncRefreshAction {
  type: typeof SYNC_REFRESH;
  payload: { reason: SyncReason };
}

export interface AppResumedAction {
  type: typeof APP_RESUMED;
  payload: { gapMs: number };
}

export const syncRefresh = (reason: SyncReason): SyncRefreshAction => ({
  type: SYNC_REFRESH,
  payload: { reason },
});

export const appResumed = (gapMs: number): AppResumedAction => ({
  type: APP_RESUMED,
  payload: { gapMs },
});
